import type { PDFDocumentProxy } from "pdfjs-dist";
import { getPdfDocument } from "./pdfjs-lib";

/**
 * PDF の 1 ページ目から JPEG サムネイルを生成する。
 *
 * 呼び出し元（file-store の pushThumbStore）は Promise.all で並列に待つため、
 * 1 件の失敗で他ファイルのサムネイルまで巻き添えにしないよう、この関数は
 * reject せず undefined を返す。Blob URL と PDF ワーカーの解放も必ず行う。
 *
 * @param file  PDFファイル
 */
export const getPdfThumbnail = async (
  file: File,
): Promise<File | undefined> => {
  const pdfUrl = URL.createObjectURL(file);
  let pdf: PDFDocumentProxy | undefined;
  try {
    pdf = await getPdfDocument({
      url: pdfUrl,
      verbosity: 0, // Warning: TT: undefined function: 3 の抑制
    });

    const canvas = document.createElement("canvas") as HTMLCanvasElement;
    const page = await pdf.getPage(1); // 1ページ目を取得
    const viewport = page.getViewport({ scale: 1.0 });
    const canvasContext = canvas.getContext("2d") as CanvasRenderingContext2D;
    canvas.style.display = "none";
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    await page.render({
      canvas,
      canvasContext,
      viewport,
    }).promise;
    const type = "image/jpeg";
    const thumbUrl = canvas.toDataURL(type);
    const blob = await (await fetch(thumbUrl)).blob();
    const fileName = `${file.name + file.size}_tmb.jpg`;
    return new File([blob], fileName, { type: type });
  } catch (err: unknown) {
    if (err instanceof Error && err.name === "PasswordException") {
      console.log("パスワード付きPDFはサムネイルを作成することができません");
    } else {
      console.error("PDF thumbnail generation failed", err);
    }
    return undefined;
  } finally {
    // getDocument は PDF ごとに専用ワーカーを起こすため、destroy しないと
    // WASM デコーダを抱えたワーカーが SPA のセッション中ずっと残る。
    await pdf?.destroy();
    URL.revokeObjectURL(pdfUrl);
  }
};
